/**
 * §24.3 — the permission vocabulary. A MIRROR of
 * `backend/src/common/constants/permissions.ts`, key for key and in the same order;
 * `test/permissions.test.ts` asserts the two are deep-equal. M2/D-05 is what drift costs: the
 * dashboard gated its branch picker on `settings.view` for months purely because `branches.view`
 * had never reached this file.
 */
export const PERMISSIONS = {
  orders: ['view', 'create', 'update', 'send', 'cancel', 'void', 'reopen'],
  sessions: ['view', 'create', 'update', 'close', 'transfer', 'merge', 'split'],
  parties: ['view', 'create', 'update', 'close'],
  checks: ['view', 'create', 'update', 'move_charge', 'split', 'merge', 'close'],
  tables: ['view', 'create', 'update', 'delete', 'transfer', 'block', 'reserve'],
  resources: ['view', 'use', 'reserve', 'maintain', 'create', 'update', 'delete'],
  resource_sessions: ['view', 'start', 'pause', 'resume', 'extend', 'stop'],
  payments: ['view', 'create', 'refund'],
  discounts: ['view', 'apply', 'approve'],
  /**
   * C-13 / §24.5 — the manager approval queue is its own authority, not a by-product of
   * `discounts.approve`. `view` reads the queue; the five `approve_*` keys each sign one §24.5
   * gate, and `approve` is the blanket "may sign any of them". Signing a 15% discount and signing
   * a stock write-off are different decisions with different blast radii.
   */
  approvals: [
    'view',
    'approve',
    /**
     * Approve a request you raised yourself. §24.5 lists what needs a sign-off but not who may
     * give it; a second signature is the safer default, so self-approval stays refused unless
     * this key is held — which lets a venue whose manager is its only approver work, without
     * deleting the rule for everyone else.
     */
    'approve_own',
    'approve_discount',
    'approve_void',
    'approve_price_override',
    'approve_refund',
    'approve_stock_adjustment',
  ],
  /**
   * S-41 — `define` is separate from `adjust`: different authorities, very different blast radii.
   * `adjust` moves the ledger, is already metered by `evaluateStockAdjustment` (X-03) and shows
   * up in variance reporting. `define` changes what a thing IS: altering an item's `unitId`
   * silently re-denominates every movement ever recorded against it, because `levels()` labels
   * the summed quantity with the item's CURRENT unit — 12500 grams starts reading as "12500 kg",
   * with no migration and no warning — and retiring an item makes a whole recipe unsaveable. A
   * shift manager who should certainly post a waste probably should not redefine what coffee is.
   */
  inventory: ['view', 'adjust', 'transfer', 'define'],
  menu: ['view', 'create', 'update', 'delete', 'price_update'],
  /**
   * I-06 — packages, entitlements and coupons used to borrow `menu.*`, `orders.*`, `customers.*`
   * and `discounts.*` because the catalogue was pinned at 100 keys. Each is a distinct grant:
   *
   * - `packages.*` authors and sells a prepaid bundle. Authoring is a catalogue act, but `sell`
   *   mints stored value against a participant — not the same authority as `orders.create`
   *   (every waiter holds that).
   * - `entitlements.*` spends or writes off a guest's already-paid balance. Drawing minutes down
   *   is not an order-line edit and cancelling a grant is not `orders.void`.
   * - `coupons.*` was gated on `customers.create/update/delete`, so the right to edit a customer
   *   record was silently the right to issue and kill discount codes. `validate` is separate from
   *   `view` on purpose: a till must check one code without reading every coupon issued.
   *
   * Promotions were deliberately NOT split — a promotion is a pricing rule, so creating one is
   * the same authority as repricing the menu (`menu.*`), and reading the offers feed is the
   * `discounts.view` authority it already used. See the note on `promotions.controller.ts`.
   */
  packages: ['view', 'create', 'update', 'delete', 'sell'],
  entitlements: ['view', 'consume', 'cancel'],
  coupons: ['view', 'validate', 'create', 'update', 'redeem', 'disable'],
  floor_map: ['view', 'create', 'update', 'delete', 'publish'],
  kitchen: ['view', 'update', 'recall', 'prioritize'],
  service_requests: ['view', 'accept', 'resolve', 'escalate'],
  reservations: ['view', 'create', 'update', 'cancel'],
  customers: ['view', 'create', 'update', 'delete'],
  reports: ['view', 'export'],
  users: ['view', 'create', 'update', 'delete'],
  roles: ['view', 'create', 'update', 'delete'],
  devices: ['view', 'register', 'revoke'],
  /**
   * I-10 — branch identity is reference data nearly every screen needs for its branch picker, so
   * reading the list must not require `settings.view`, which operations staff correctly do not
   * hold. Reads only: create/edit/delete stays on `settings.update`, and guest Wi-Fi credentials
   * stay on `settings.view` because they are a secret, not reference data.
   */
  branches: ['view'],
  settings: ['view', 'update'],
  /**
   * `manage_content` is the marketing site's copy — tarabeeza.com's own words,
   * edited from this console rather than through a pull request against the
   * landing repository. In THIS family and not a new one because the authority
   * is the same authority: it is the platform's own site, so `PLATFORM_ADMIN`
   * picks the key up from `ALL_PERMISSION_KEYS` and every tenant role drops it
   * through the `!startsWith('platform.')` filter that already exists.
   */
  platform: ['view', 'manage_tenants', 'manage_plans', 'manage_flags', 'support', 'manage_content'],
} as const;

export type PermissionResource = keyof typeof PERMISSIONS;

export const ALL_PERMISSION_KEYS: string[] = Object.entries(PERMISSIONS).flatMap(
  ([resource, actions]) => (actions as readonly string[]).map((a) => `${resource}.${a}`),
);

/** Client-side gate. The server re-checks everything — this only hides UI. */
export function can(
  user: { permissions: string[]; isPlatformAdmin?: boolean } | null | undefined,
  ...required: string[]
): boolean {
  if (!user) return false;
  if (user.isPlatformAdmin) return true;
  return required.every((key) => user.permissions.includes(key));
}

export function canAny(
  user: { permissions: string[]; isPlatformAdmin?: boolean } | null | undefined,
  ...required: string[]
): boolean {
  if (!user) return false;
  if (user.isPlatformAdmin) return true;
  return required.some((key) => user.permissions.includes(key));
}
